/* important: always have up to 6 options MAX */

import React, { useState } from 'react';
import { useController } from './controller';
import { translations, LanguageKeys } from './translation';
import * as S from './styles';
import { useParticles } from './particles';
import DefaultLoading from '../../components/DefaultLoading';
import ImprovedLoading from '../../components/ImprovedLoading';
import BetterExplain from '../../components/BetterExplain';
import PremiumPopUp from '../../components/PremiumPopUp';
import moment from 'moment-timezone';

const ListComponent = (props: {
    controller: any,
    index: number,
    pageCount: number,
    pageChange: (data: any) => void,
    items?: any[]
}) => {
    const controller = props.controller;
    const currentLanguage = controller.currentLanguage as LanguageKeys;

    return (
        <S.listContainer index={props.index}>
            <S.listItems>
                {(props.items?.length ?? 0) > 0 ? (
                    (props.items ?? []).map((item: any, index: number) => (
                        <S.listItem key={index + controller.general.randomString(10)} onClick={item.execFunction ?? (() => { })}>
                            <S.listItemHeader>
                                <S.listItemTitle>{item.name}</S.listItemTitle>
                            </S.listItemHeader>
                            <S.listItemBody>
                                {(item.bodyItems ?? []).map((bodyItem: any, index: number) => (
                                    <S.listItemBodyInfo>{bodyItem}</S.listItemBodyInfo>
                                ))}
                            </S.listItemBody>
                        </S.listItem>
                    ))
                ) : (
                    <S.noItems>{translations[currentLanguage].noItems}</S.noItems>
                )}
            </S.listItems>
            <S.reactPaginate
                nextLabel=">"
                onPageChange={props.pageChange}
                pageRangeDisplayed={3}
                marginPagesDisplayed={2}
                pageCount={props.pageCount}
                previousLabel="<"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                breakLabel="..."
                breakClassName="page-item"
                breakLinkClassName="page-link"
                containerClassName="pagination"
                activeClassName="active"
                renderOnZeroPageCount={null}
            />
        </S.listContainer>
    )
}

const Main = (props: any) => {
    const controller = useController();
    const currentLanguage = controller.currentLanguage as LanguageKeys;

    return (
        <S.mainContainer>
            <PremiumPopUp setActive={controller.setPremiumPopUpActive} active={controller.premiumPopUpActive} />
            <BetterExplain setActive={controller.setBetterExplainingActive} active={controller.betterExplainingActive} explanation={controller.betterExplaining ?? translations[controller.currentLanguage].processing} term={controller.betterExplainingTerm} />
            <ImprovedLoading loadMoreTips={controller.getTopicsTips} active={controller.loading} curiosities={controller.curiosities ?? []} />
            <DefaultLoading active={controller.redirectLoading} />
            {controller.youtubeVideoModal && (
                <S.youtubeModal>
                    <S.youtubeModalContent onClick={() => controller.setYoutubeVideoModal(false)}>
                        <S.youtubeModalClose onClick={() => controller.setYoutubeVideoModal(false)} icon={controller.general.icons.faXmark} />
                        <S.youtubeModalVideo onClick={(e: any) => {
                            e.stopPropagation();
                        }} src={controller.currentYoutubeVideo?.link?.replace('https://youtu.be/', 'https://youtube.com/embed/')} frameBorder="0" allowFullScreen></S.youtubeModalVideo>
                    </S.youtubeModalContent>
                </S.youtubeModal>
            )}
            <DefaultLoading active={controller.simpleLoading} />
            {controller.choosingOptions ? (
                <S.actions>
                    <S.actionsHeader>
                        {/* @ts-ignore */}
                        {controller.actionOptions[controller.page]?.title ?? 'Not Found'}
                    </S.actionsHeader>
                    <S.actionsOptions>
                        {/* @ts-ignore */}
                        {controller.actionOptions[controller.page]?.options.map((option, index) => (
                            <S.actionOption key={index + controller.general.randomString(10)} index={index} onClick={option.function}>
                                <S.actionIcon icon={option.icon} />
                                <S.actionText>{option.text}</S.actionText>
                            </S.actionOption>
                        ))}
                    </S.actionsOptions>
                </S.actions>
            ) : (
                <>
                    {controller.page == 'studySession' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.actionsHeader>
                                {translations[controller.currentLanguage].questionLearnAbout}
                            </S.actionsHeader>
                            <S.inputForAI
                                index={0}
                                placeholder={translations[controller.currentLanguage].inputAiPlaceholder}
                                contentEditable
                                suppressContentEditableWarning
                                onPaste={(e: any) => {
                                    e.preventDefault();
                                    const text = e.clipboardData.getData('text/plain');
                                    controller.general.stripeAllHtmlTags(controller.setInputForAi(text) as any)
                                    e.currentTarget.innerText = text;
                                }}
                                onKeyUp={(e: any) => {
                                    controller.setInputForAi(controller.general.stripeAllHtmlTags(e?.target?.innerText) ?? '')
                                }}
                                onKeyDown={(e: any) => {
                                    if (e.key == 'Enter') {
                                        controller.topicLearn();
                                        if(!controller.general.canRequestAI()) return;
                                        controller.setPage('topicAnswer');
                                        controller.setCuriosities([])
                                        controller.setChoosingOptions(false);
                                    }
                                }}
                            />
                            <S.buttonOptions>
                                <S.optionButton onClick={() => {
                                    controller.topicLearn();
                                    if(!controller.general.canRequestAI()) return;
                                    controller.setPage('topicAnswer');
                                    controller.setCuriosities([])
                                    controller.setChoosingOptions(false);
                                }} index={1}>{translations[controller.currentLanguage].learnAbout}</S.optionButton>
                                <S.optionButton index={2} onClick={() => {
                                    controller.topicLearn(controller.general.randomTopics[controller.general.randomNumber(0, controller.general.randomTopics.length - 1)]);
                                    if(!controller.general.canRequestAI()) return;
                                    controller.setPage('topicAnswer');
                                    controller.setCuriosities([])
                                    controller.setChoosingOptions(false);
                                }}>{translations[controller.currentLanguage].learnAboutRandom}</S.optionButton>
                            </S.buttonOptions>
                        </S.actions>
                    )}
                    {controller.page == 'topicAnswer' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.sessionTime>
                                <S.sessionTimeText>
                                    {controller.general.turnTimestampIntoTimer(controller.studyTime)}
                                </S.sessionTimeText>
                            </S.sessionTime>
                            <S.actionsHeader>
                                {controller.currentTopic?.name ?? translations[controller.currentLanguage].topicAnswer}
                            </S.actionsHeader>
                            <S.outputForAI id="learnTopicOutput" index={0}>
                                {controller.general.formatAIText(controller.currentTopic?.answer ?? '') ?? translations[controller.currentLanguage].processingTopic}
                                {controller.currentTopic?.searchTerms?.length > 0 ? (
                                    <>
                                        {'\n\n'}
                                        <span className='span_title'>{translations[controller.currentLanguage].clickToExplain}</span>
                                        {'\n'}
                                        {controller.currentTopic?.searchTerms.map((term: any, index: number) => (
                                            <>
                                                <span onClick={() => {
                                                    controller.betterExplainTerm(term.term, term.id);
                                                    controller.setBetterExplainingTerm(term.term);
                                                }} className='span_action' key={'terms_' + index}>{term.term}</span>
                                                {'\n'}
                                            </>
                                        ))}
                                    </>
                                ) : ''}
                                {controller.currentTopic?.relatedTopics?.length > 0 ? (
                                    <>
                                        {'\n'}
                                        <span className='span_title'>{translations[controller.currentLanguage].relatedTopics}</span>
                                        {'\n'}
                                        {controller.currentTopic?.relatedTopics.map((topic: string, index: number) => (
                                            <>
                                                <span onClick={() => controller.topicLearn(topic)} className='span_action' key={'related_' + index}>{topic}</span>
                                                {'\n'}
                                            </>
                                        ))}
                                    </>
                                ) : ''}
                                {controller.session?.topics?.length > 0 ? (
                                    <>
                                        {'\n'}
                                        <span className='span_title' style={{
                                            cursor: 'pointer',
                                        }} onClick={() => {
                                            controller.setShowSections((showSections: any) => ({ ...showSections, sessionTopics: !showSections.sessionTopics }));
                                        }}>
                                            {translations[controller.currentLanguage].sessionTopics}
                                            <S.showSectionIcon icon={controller.showSections?.sessionTopics ? controller.general.icons.faChevronUp : controller.general.icons.faChevronDown} />
                                        </span>
                                        {'\n'}
                                        {controller.showSections?.sessionTopics && (
                                            controller.session?.topics.map((topic: any, index: number) => (
                                                <>
                                                    <span onClick={() => controller.setCurrentTopic(topic)} className='span_action' key={'allTopics_' + index}>{topic?.name}</span>
                                                    {'\n'}
                                                </>
                                            ))
                                        )}
                                    </>
                                ) : ''}
                                {controller.session?.quizzes?.length > 0 ? (
                                    <>
                                        {'\n'}
                                        <span className='span_title' style={{
                                            cursor: 'pointer',
                                        }} onClick={() => {
                                            controller.setShowSections((showSections: any) => ({ ...showSections, quizzes: !showSections.quizzes }));
                                        }}>
                                            {translations[controller.currentLanguage].tests}
                                            <S.showSectionIcon icon={controller.showSections?.quizzes ? controller.general.icons.faChevronUp : controller.general.icons.faChevronDown} />
                                        </span>
                                        {'\n'}
                                        {controller.showSections?.quizzes && (
                                            controller.session?.quizzes?.map((quiz: any, index: number) => (
                                                <>
                                                    <span onClick={() => {
                                                        controller.setQuiz(quiz)
                                                        controller.setPage('quizResults');
                                                        controller.setCurrentQuizQuestionIndex(0);
                                                    }} className='span_action' key={'allQuizzes_' + index}>{quiz?.name}</span>
                                                    {'\n'}
                                                </>
                                            ))
                                        )}
                                    </>
                                ) : ''}
                                {controller.session?.flashcards?.length > 0 ? (
                                    <>
                                        {'\n'}
                                        <span className='span_title' style={{
                                            cursor: 'pointer',
                                        }} onClick={() => {
                                            controller.setShowSections((showSections: any) => ({ ...showSections, flashcards: !showSections.flashcards }));
                                        }}>
                                            {translations[controller.currentLanguage].flashcards}
                                            <S.showSectionIcon icon={controller.showSections?.quizzes ? controller.general.icons.faChevronUp : controller.general.icons.faChevronDown} />
                                        </span>
                                        {'\n'}
                                        {controller.showSections?.quizzes && (
                                            controller.session?.flashcards?.map((flashcards: any, index: number) => (
                                                <>
                                                    <span onClick={() => {
                                                        controller.setFlashcards(flashcards)
                                                        controller.setPage('showFlashcards');
                                                    }} className='span_action' key={'allQuizzes_' + index}>{flashcards?.name}</span>
                                                    {'\n'}
                                                </>
                                            ))
                                        )}
                                    </>
                                ) : ''}
                                {controller.youtubeVideos?.length > 0 ? (
                                    <>
                                        {'\n'}
                                        <span className='span_title'>
                                            {translations[controller.currentLanguage].relatedVideos}
                                            <S.refreshYoutubeVideosIcon icon={controller.general.icons.faRotate} onClick={() => {
                                                controller.setYoutubeVideos(controller.general.shuffleArray(controller.youtubeVideos ?? [], true));
                                            }} />
                                        </span>
                                        {'\n'}
                                        <S.youtubeVideosList>
                                            {controller.youtubeVideos?.slice(0, 5).map((video: any, index: number) => (
                                                <S.youtubeVideo key={video?.link + `_${index}`} index={index} onClick={() => {
                                                    controller.setYoutubeVideoModal(true);
                                                    controller.setCurrentYoutubeVideo(video);
                                                }}>
                                                    <S.youtubeVideoImage src={video.thumbnail} alt={video.title} />
                                                    <S.youtubeVideoTitle>{video.title}</S.youtubeVideoTitle>
                                                </S.youtubeVideo>
                                            ))}
                                        </S.youtubeVideosList>
                                    </>
                                ) : ''}
                            </S.outputForAI>
                            <S.askAboutTopicWrapper onSubmit={(e: any) => {
                                e.preventDefault();
                                if (controller.inputForAi.length > 0) {
                                    controller.topicLearn(controller.inputForAi);
                                    e.currentTarget.reset();
                                }
                                else {
                                    controller.general.toast.error(translations[controller.currentLanguage].typeSomething);
                                }
                            }}>
                                <S.askAboutTopicInput
                                    placeholder={translations[controller.currentLanguage].anyQuestions}
                                    type="text"
                                    onKeyUp={(e: any) => {
                                        controller.setInputForAi(controller.general.stripeAllHtmlTags(e?.target?.value) ?? '')
                                    }}
                                />
                                <S.askAboutTopicButton>
                                    <S.askAboutTopicIcon icon={controller.general.icons.faSearch} />
                                </S.askAboutTopicButton>
                            </S.askAboutTopicWrapper>
                            <S.buttonOptions className='dontShowOnMobile'>
                                <S.optionButton onClick={() => {
                                    controller.setPage('selectTopicsTests')
                                }} index={1}>{translations[controller.currentLanguage].makeTest}</S.optionButton>
                                <S.optionButton onClick={() => {
                                    controller.setPage('selectTopicsFlashcard')
                                }} index={2}>{translations[controller.currentLanguage].createFlashcards}</S.optionButton>
                                <S.optionButton onClick={() => {
                                    controller.setPage('studySession');
                                    controller.setCuriosities([])
                                    controller.setChoosingOptions(false);
                                    controller.setSessionSearchedTopics([]);
                                }} index={3}>{translations[controller.currentLanguage].newSession}</S.optionButton>
                            </S.buttonOptions>
                            <S.buttonOptionsMobile>
                                <S.buttonMobileOption index={1} onClick={() => {
                                    controller.setPage('selectTopicsTests')
                                }}>
                                    <S.buttonMobileIcon icon={controller.general.icons.faClipboardList} />
                                    <S.buttonMobileText>{translations[controller.currentLanguage].makeTest}</S.buttonMobileText>
                                </S.buttonMobileOption>
                                <S.buttonMobileOption index={2} onClick={() => {
                                    controller.setPage('selectTopicsFlashcard')
                                }}>
                                    <S.buttonMobileIcon icon={controller.general.icons.faNoteSticky} />
                                    <S.buttonMobileText>{translations[controller.currentLanguage].createFlashcards}</S.buttonMobileText>
                                </S.buttonMobileOption>
                                <S.buttonMobileOption index={3} onClick={() => {
                                    controller.setPage('studySession');
                                    controller.setCuriosities([])
                                    controller.setChoosingOptions(false);
                                    controller.setSessionSearchedTopics([]);
                                }}>
                                    <S.buttonMobileIcon icon={controller.general.icons.faBookOpen} />
                                    <S.buttonMobileText>{translations[controller.currentLanguage].newSession}</S.buttonMobileText>
                                </S.buttonMobileOption>
                            </S.buttonOptionsMobile>
                        </S.actions>
                    )}
                    {controller.page == 'myHistory' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.actionsHeader>
                                {translations[currentLanguage].myHistory}
                            </S.actionsHeader>
                            <ListComponent
                                index={1}
                                controller={controller}
                                pageCount={controller.allSessions.pages ?? 0}
                                pageChange={(e: any) => {
                                    controller.updateAllSessions(e.selected + 1);
                                }}
                                items={(controller.allSessions?.sessions ?? []).map((item: any) => {
                                    return {
                                        name: translations[currentLanguage].session + ': ' + item.name,
                                        execFunction: () => {
                                            controller.getStudySession(item?._id);
                                            controller.setPage('topicAnswer');
                                        },
                                        bodyItems: [
                                            translations[controller.currentLanguage].topics + ': ' + item.topics?.length,
                                            translations[controller.currentLanguage].tests + ': ' + item.quizzes?.length,
                                            translations[controller.currentLanguage].flashcards + ': ' + item.flashcards?.length,
                                            translations[controller.currentLanguage].studyTime + ': ' + (controller.general.turnTimestampIntoTimer(item.studyTime) == '00' ? '0s' : controller.general.turnTimestampIntoTimer(item.studyTime)),
                                            translations[controller.currentLanguage].lastActivityDate + ': ' + controller.general.formatDateWithTimezone(item.updatedAt),
                                        ]
                                    }
                                }) ?? []}
                            />
                            <S.buttonOptions>
                                <S.optionButton onClick={() => {
                                    controller.setPage('studySession');
                                    controller.setCuriosities([])
                                    controller.setChoosingOptions(false);
                                    controller.setSessionSearchedTopics([]);
                                }} index={2}>{translations[controller.currentLanguage].newSession}</S.optionButton>
                                <S.optionButton onClick={() => {
                                    controller.setPage('main');
                                    controller.setCuriosities([])
                                    controller.setChoosingOptions(true);
                                    controller.setSessionSearchedTopics([]);
                                }} index={3}>{translations[controller.currentLanguage].return}</S.optionButton>
                            </S.buttonOptions>
                        </S.actions>
                    )}
                    {controller.page == 'selectTopicsTests' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.actionsHeader>
                                {translations[currentLanguage].selectTopics}:
                            </S.actionsHeader>
                            <S.topicsChecklist>
                                {(controller.session?.topics ?? []).map((topic: any, index: number) => (
                                    <S.topicItem active={controller.selectedTopics?.find((item: any) => item.id == topic._id)} key={index + controller.general.randomString(10)} onClick={() => {
                                        controller.setSelectedTopics((selectedTopics: any) => {
                                            const found = selectedTopics.find((item: any) => item.id == topic._id);
                                            if (found) {
                                                return selectedTopics.filter((item: any) => item.id != topic._id);
                                            }
                                            else {
                                                return [...selectedTopics, { id: topic._id, name: topic.name }];
                                            }
                                        })
                                    }}>
                                        <S.topicItemCheckWrapper>
                                            <S.topicItemCheck icon={controller.selectedTopics?.find((item: any) => item.id == topic._id) ? controller.general.icons.faCheck : controller.general.icons.faCircle} />
                                            <S.topicItemText>{topic.name}</S.topicItemText>
                                        </S.topicItemCheckWrapper>
                                    </S.topicItem>
                                ))}
                            </S.topicsChecklist>
                            <S.buttonOptions>
                                <S.optionButton onClick={() => {
                                    controller.createQuiz()
                                }} index={2}>{translations[controller.currentLanguage].create} {translations[controller.currentLanguage].quiz}</S.optionButton>
                            </S.buttonOptions>
                        </S.actions>
                    )}
                    {controller.page == 'selectTopicsFlashcard' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.actionsHeader>
                                {translations[currentLanguage].selectTopics}:
                            </S.actionsHeader>
                            <S.topicsChecklist>
                                {(controller.session?.topics ?? []).map((topic: any, index: number) => (
                                    <S.topicItem active={controller.selectedTopics?.find((item: any) => item.id == topic._id)} key={index + controller.general.randomString(10)} onClick={() => {
                                        controller.setSelectedTopics((selectedTopics: any) => {
                                            const found = selectedTopics.find((item: any) => item.id == topic._id);
                                            if (found) {
                                                return selectedTopics.filter((item: any) => item.id != topic._id);
                                            }
                                            else {
                                                return [...selectedTopics, { id: topic._id, name: topic.name }];
                                            }
                                        })
                                    }}>
                                        <S.topicItemCheckWrapper>
                                            <S.topicItemCheck icon={controller.selectedTopics?.find((item: any) => item.id == topic._id) ? controller.general.icons.faCheck : controller.general.icons.faCircle} />
                                            <S.topicItemText>{topic.name}</S.topicItemText>
                                        </S.topicItemCheckWrapper>
                                    </S.topicItem>
                                ))}
                            </S.topicsChecklist>
                            <S.buttonOptions>
                                <S.optionButton onClick={() => {
                                    controller.createFlashcards()
                                }} index={2}>{translations[controller.currentLanguage].create} Flashcard</S.optionButton>
                            </S.buttonOptions>
                        </S.actions>
                    )}
                    {controller.page == 'showFlashcards' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.flashcardsTitle>
                                {controller.flashcards?.name ?? translations[controller.currentLanguage].flashcards}
                            </S.flashcardsTitle>
                            <S.flashcardsArrayWrapper>
                                <S.flashcardsArray
                                    frontContentStyle={{
                                        backgroundColor: 'black',
                                        color: 'var(--palette-one-white)',
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        padding: '1rem',
                                        borderRadius: '1rem',
                                        textAlign: 'center',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        width: '100%',
                                        boxShadow: '0px 0px 10px 0px rgba(255,255,255,0.4)',
                                        border: '1px solid var(--palette-one-white)'
                                    }}
                                    backContentStyle={{
                                        backgroundColor: 'var(--palette-one-white)',
                                        color: 'black',
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        padding: '1rem',
                                        borderRadius: '1rem',
                                        textAlign: 'center',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        width: '100%',
                                        boxShadow: '0px 0px 10px 0px rgba(255,255,255,0.4)',
                                        border: '1px solid var(--palette-one-black)'
                                    }}
                                    cards={(controller.flashcards?.items ?? [])?.map((flashcard: any) => {
                                        return {
                                            id: flashcard._id,
                                            frontHTML: flashcard.question,
                                            backHTML: flashcard.answer,
                                        }
                                    })}
                                    FlashcardArrayStyle={{
                                        color: 'var(--palette-one-white)',
                                        width: '100%',
                                    }}
                                    cycle={true}
                                />
                            </S.flashcardsArrayWrapper>
                            <S.buttonOptions>
                                <S.optionButton onClick={() => {
                                    controller.setPage('flashcardsHistory');
                                }} index={2}>{translations[controller.currentLanguage].myFlashcards}</S.optionButton>
                                <S.optionButton onClick={() => {
                                    controller.setPage('topicAnswer');
                                    controller.setChoosingOptions(false);
                                    controller.setCurrentQuizQuestionIndex(0);
                                }} index={3}>{translations[controller.currentLanguage].goBackToSession}</S.optionButton>
                            </S.buttonOptions>
                        </S.actions>
                    )}
                    {controller.page == 'takingQuiz' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.questionsTitle>
                                [{controller.currentQuizQuestionIndex + 1}/{controller.quiz?.questions.length}] - {controller.quiz?.questions[controller.currentQuizQuestionIndex]?.question}
                            </S.questionsTitle>
                            <S.questionOptions>
                                {(controller.quiz?.questions[controller.currentQuizQuestionIndex]?.questionOptions ?? []).map((option: any, index: number) => (
                                    <S.questionUniqueOption active={option.id == controller.quiz?.questions[controller.currentQuizQuestionIndex]?.studentAnswer} key={index + controller.general.randomString(10)} onClick={() => {
                                        controller.setQuizAnswer(option.id)
                                    }}>
                                        <S.questionUniqueOptionWrapper>
                                            <S.questionOptionCheck icon={option.id == controller.quiz?.questions[controller.currentQuizQuestionIndex]?.studentAnswer ? controller.general.icons.faCheck : controller.general.icons.faCircle} />
                                            <S.questionOptionText>{option.answer}</S.questionOptionText>
                                        </S.questionUniqueOptionWrapper>
                                    </S.questionUniqueOption>
                                ))}
                            </S.questionOptions>
                            <S.buttonOptions className='altWithRow'>
                                {controller.currentQuizQuestionIndex > 0 && (
                                    <S.optionButton className='altWithRowOption' onClick={() => {
                                        controller.setCurrentQuizQuestionIndex((index: number) => {
                                            if (index - 1 < 0) {
                                                return index;
                                            }
                                            else {
                                                return index - 1;
                                            }
                                        })
                                    }} index={2}>{translations[controller.currentLanguage].previous}</S.optionButton>
                                )}
                                {controller.currentQuizQuestionIndex < controller.quiz?.questions.length - 1 ? (
                                    <S.optionButton className='altWithRowOption' onClick={() => {
                                        controller.setCurrentQuizQuestionIndex((index: number) => {
                                            if (index + 1 >= controller.quiz?.questions.length) {
                                                return index;
                                            }
                                            else {
                                                return index + 1;
                                            }
                                        })
                                    }} index={2}>{translations[controller.currentLanguage].next}</S.optionButton>
                                ) : (
                                    <S.optionButton className='altWithRowOption' onClick={() => {
                                        controller.setPage('quizResults');
                                        try {
                                            controller.general.api.post(`/quiz/update/${controller.quiz?._id}`, controller.quiz).then((response: any) => {
                                                if (response.data.status == 200) {
                                                    controller.setQuiz(response.data.data.quiz);
                                                }
                                                else {
                                                    controller.general.toast.error(translations[controller.currentLanguage].error);
                                                }
                                            })
                                        } catch (err) {
                                            controller.general.toast.error(translations[controller.currentLanguage].error);
                                        }
                                    }} index={2}>{translations[controller.currentLanguage].end}</S.optionButton>
                                )}
                            </S.buttonOptions>
                        </S.actions>
                    )}
                    {controller.page == 'quizResults' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.questionsTitle>
                                {translations[controller.currentLanguage].quizResults}
                            </S.questionsTitle>
                            <S.outputForQuestions index={0}>
                                {translations[controller.currentLanguage].totalQuestions}: {controller.quiz?.questions.length} <br />
                                {translations[controller.currentLanguage].correctQuestions}: {controller.quiz?.questions.filter((question: any) => question?.correctAnswer == question?.studentAnswer)?.length ?? 0} <br />
                                {translations[controller.currentLanguage].wrongQuestions}: {controller.quiz?.questions.filter((question: any) => question?.correctAnswer != question?.studentAnswer)?.length ?? 0} <br />
                                {translations[controller.currentLanguage].unansweredQuestions}: {controller.quiz?.questions.filter((question: any) => question?.studentAnswer == null)?.length ?? 0} <br />

                                {controller.quiz?.questions?.map((question: any, index: number) => (
                                    <>
                                        <br />
                                        {index + 1}) {question.question}
                                        <br />
                                        {translations[controller.currentLanguage].yourAnswer}: {question.questionOptions?.find((option: any) => option.id == question.studentAnswer)?.answer ?? translations[controller.currentLanguage].noAnswer}
                                        <br />
                                        {translations[controller.currentLanguage].correctAnswer}: {question.questionOptions?.find((option: any) => option.id == question.correctAnswer)?.answer ?? translations[controller.currentLanguage].noAnswer}
                                        <br />
                                        ({question.correctAnswer == question.studentAnswer ? translations[controller.currentLanguage].isCorrect : translations[controller.currentLanguage].isWrong})
                                        <br />
                                    </>
                                ))}
                            </S.outputForQuestions>
                            <S.buttonOptions>
                                <S.optionButton onClick={() => {
                                    controller.retryQuiz(controller.quiz?.quizModelId);
                                }} index={2}>{translations[controller.currentLanguage].takeQuizAgain}</S.optionButton>
                                <S.optionButton onClick={() => {
                                    controller.setPage('topicAnswer');
                                    controller.setChoosingOptions(false);
                                    controller.setCurrentQuizQuestionIndex(0);
                                }} index={3}>{translations[controller.currentLanguage].goBackToSession}</S.optionButton>
                            </S.buttonOptions>
                        </S.actions>
                    )}
                    {controller.page == 'quizHistory' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.actionsHeader>
                                {translations[currentLanguage].myQuizzes}
                            </S.actionsHeader>
                            <ListComponent
                                index={1}
                                controller={controller}
                                pageCount={controller.allQuizzes.pages ?? 0}
                                pageChange={(e: any) => {
                                    controller.updateAllQuizzes(e.selected + 1);
                                }}
                                items={(controller.allQuizzes?.quizzes ?? []).map((item: any) => {
                                    return {
                                        name: translations[currentLanguage].quiz + ': ' + item.name,
                                        execFunction: () => {
                                            controller.getQuiz(item?._id);
                                            controller.setPage('quizResults');
                                        },
                                        bodyItems: [
                                            `${translations[controller.currentLanguage].totalQuestions}: ${item?.questions.length}`,
                                            `${translations[controller.currentLanguage].correctQuestions}: ${item?.questions.filter((question: any) => question?.correctAnswer == question?.studentAnswer)?.length ?? 0}`,
                                            `${translations[controller.currentLanguage].wrongQuestions}: ${item?.questions.filter((question: any) => question?.correctAnswer != question?.studentAnswer)?.length ?? 0}`,
                                            `${translations[controller.currentLanguage].unansweredQuestions}: ${item?.questions.filter((question: any) => question?.studentAnswer == null)?.length ?? 0}`,
                                            `${translations[controller.currentLanguage].createdAt}: ${controller.general.formatDateWithTimezone(item.createdAt)}`,
                                        ]
                                    }
                                }) ?? []}
                            />
                            <S.buttonOptions>
                                <S.optionButton onClick={() => {
                                    controller.setPage('main');
                                    controller.setCuriosities([])
                                    controller.setChoosingOptions(true);
                                    controller.setSessionSearchedTopics([]);
                                }} index={2}>{translations[controller.currentLanguage].return}</S.optionButton>
                            </S.buttonOptions>
                        </S.actions>
                    )}
                    {controller.page == 'flashcardsHistory' && (
                        <S.actions>
                            <S.goBack onClick={() => {
                                controller.setPage('main');
                                controller.setCuriosities([])
                                controller.setChoosingOptions(true);
                                controller.setSessionSearchedTopics([]);
                            }}>
                                <S.goBackText>{translations[controller.currentLanguage].return}</S.goBackText>
                                <S.textUnderline />
                            </S.goBack>
                            <S.actionsHeader>
                                {translations[currentLanguage].myHistory}
                            </S.actionsHeader>
                            <ListComponent
                                index={1}
                                controller={controller}
                                pageCount={controller.allSessions.pages ?? 0}
                                pageChange={(e: any) => {
                                    controller.updateAllSessions(e.selected + 1);
                                }}
                                items={(controller.allFlashcards?.flashcards ?? []).map((item: any) => {
                                    return {
                                        name: translations[currentLanguage].flashcards + ': ' + item.name,
                                        execFunction: () => {
                                            controller.getFlashcards(item?._id);
                                            controller.setPage('showFlashcards');
                                        },
                                        bodyItems: [
                                            translations[controller.currentLanguage].cardsAmount + ': ' + item.items?.length,
                                            translations[controller.currentLanguage].lastActivityDate + ': ' + controller.general.formatDateWithTimezone(item.updatedAt),
                                        ]
                                    }
                                }) ?? []}
                            />
                            <S.buttonOptions>
                                <S.optionButton onClick={() => {
                                    controller.setPage('main');
                                    controller.setCuriosities([])
                                    controller.setChoosingOptions(true);
                                    controller.setSessionSearchedTopics([]);
                                }} index={3}>{translations[controller.currentLanguage].return}</S.optionButton>
                            </S.buttonOptions>
                        </S.actions>
                    )}
                </>
            )}
        </S.mainContainer>
    )
}

const MountedComponent = (props: any) => {
    const particles = useParticles();

    return (
        <>
            <Main />
            <S.particles
                id="tsparticles"
                particlesLoaded={particles.particlesLoaded as any}
                options={particles.options}
            />
        </>
    )
}

export default MountedComponent;